import React, { useState, useEffect } from 'react';
import './Navbar.css';
import { motion, AnimatePresence } from '../utils/motion';

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 40);

            // Determine active section based on scroll offset
            const sectionIds = ['home', 'about', 'services', 'connect'];
            const scrollPosition = window.scrollY + 250;

            for (let i = sectionIds.length - 1; i >= 0; i--) {
                const element = document.getElementById(sectionIds[i]);
                if (element) {
                    const top = element.offsetTop;
                    if (scrollPosition >= top) {
                        setActiveSection(sectionIds[i]);
                        break;
                    }
                }
            }
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { label: 'Home', href: '#home', id: 'home' },
        { label: 'About', href: '#about', id: 'about' },
        { label: 'Services', href: '#services', id: 'services' },
        { label: 'Connect', href: '#connect', id: 'connect' },
    ];

    const handleNavClick = (id) => {
        if (id) setActiveSection(id);
        setMenuOpen(false);
    };

    return (
        <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
            <div className="navbar__inner">
                {/* Logo */}
                <a href="#home" className="navbar__logo" onClick={() => handleNavClick('home')}>
                    <span className="navbar__logo-icon">
                        <svg width="32" height="32" viewBox="0 0 28 28" fill="none">
                            <rect width="28" height="28" rx="8" fill="url(#logoGrad)" />
                            <path d="M8 10L14 18L20 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            <defs>
                                <linearGradient id="logoGrad" x1="0" y1="0" x2="28" y2="28">
                                    <stop stopColor="#6c5ce7" />
                                    <stop offset="0.5" stopColor="#00cec9" />
                                    <stop offset="1" stopColor="#fd79a8" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </span>
                    <span className="navbar__logo-text">
                        Life Be <span className="navbar__logo-accent">Like</span>
                    </span>
                </a>

                {/* Desktop Nav Items */}
                <div className="navbar__menu navbar__menu--desktop">
                    {navItems.map((item) => {
                        const isActive = activeSection === item.id;
                        return (
                            <a
                                key={item.label}
                                href={item.href}
                                className={`navbar__link ${isActive ? 'navbar__link--active' : ''}`}
                                onClick={() => handleNavClick(item.id)}
                            >
                                {item.label}
                                {isActive && (
                                    <motion.div 
                                        className="navbar__active-pill" 
                                        layoutId="activePill"
                                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                    />
                                )}
                            </a>
                        );
                    })}
                    <motion.a
                        href="https://www.facebook.com/share/19i2p1Ab8M/?mibextid=wwXIfr"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="navbar__cta"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <span>Follow Us</span>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 13L13 1M13 1H3M13 1V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </motion.a>
                </div>

                {/* Mobile Toggle */}
                <button
                    className={`navbar__toggle ${menuOpen ? 'navbar__toggle--open' : ''}`}
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="Toggle menu"
                >
                    <span />
                    <span />
                    <span />
                </button>
            </div>

            {/* Mobile Drawer */}
            <AnimatePresence>
                {menuOpen && (
                    <motion.div 
                        className="navbar__mobile-drawer"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                        <div className="navbar__mobile-inner">
                            {navItems.map((item) => (
                                <a
                                    key={item.label}
                                    href={item.href}
                                    className={`navbar__mobile-link ${activeSection === item.id ? 'navbar__mobile-link--active' : ''}`}
                                    onClick={() => handleNavClick(item.id)}
                                >
                                    {item.label}
                                </a>
                            ))}
                            <a
                                href="https://www.facebook.com/share/19i2p1Ab8M/?mibextid=wwXIfr"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="navbar__mobile-cta"
                                onClick={() => handleNavClick()}
                            >
                                Follow Us
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
